import React, { useCallback, useMemo, useState } from 'react'
import { AppTitlePage } from '../../components/AppTitlePage'
import { AllRoutes } from '../../core/routes/AllRoutes'
import { AppBtnColor } from '../../components/btn/AppBtn'
import { useNavigate } from 'react-router'
import { useSearchParams } from 'react-router-dom'
import { AppFilterDateInput } from '../../components/AppInput/AppFilterDateInput'
import { AppFilterTimeInput } from '../../components/AppInput/AppFilterTimeInput'
import {
  useGetPlanMenuListQuery,
  useGetPlanMenuSimpleListQuery,
} from '../../core/api/PlanMenuApi'
import { useParamsControl } from '../../utils/useParamsControl'
import { IPlanMenuDto, IPlanMenuRequest } from '../../core/api/dto/PlanMenuDto'
import { AppLoader, LoaderType } from 'components/AppLoader'
import moment from 'moment'
import { AppFilterMultiDropdownWithPagination } from '../../components/AppDropdown/AppFilterMultiDropdownWithPagination'
import { useGetPlanMenuTypeListQuery } from '../../core/api/PlanMenuTypeApi'
import { IPlanMenuTypeDto } from '../../core/api/dto/PlanMenuTypeDto'
import { IBaseSimpleList } from '../../core/api/dto/BaseDto'
import { useGetGuestListTypeListQuery } from '../../core/api/GuestTypeApi'
import {
  AppTableAlignType,
  AppTableLink,
} from '../../components/AppTable/components/AppTableLink'
import { AppTableNumberBlock } from '../../components/AppTable/components/AppTableNumberBlock'
import { AppTableMobile } from '../../components/AppTable/AppTableMobile'
import { useTranslation } from 'react-i18next'

import { useDeletePlanMenu } from './hooks/useDeletePlanMenu'
import { IChooseItems } from 'core/api/dto/BuffetDishDto'
import { AppDeletePopup } from 'entities/AppDeletePopup/AppDeletePopup'
import { useGetCurrentUserQuery } from 'core/api/UserApi'
import { IHeaderData } from 'components/AppTable/AppTable'

const headerData:IHeaderData[] = [
  {
    title: 'Дата исполнения',
    colWidth: '15%',
    sort: 'date_start,date_end',
  },
  {
    title: 'Время исполнения',
    colWidth: '15%',
    sort: 'date_start__time,date_end__time',
  },
  { title: 'Тип план-меню', colWidth: '15%', sort: 'plan_menu_type__name' },
  { title: 'Тип гостя', colWidth: '15%', sort: 'plan_menu_guest_type' },
  { title: 'Название план-меню', colWidth: '15%', sort: 'name' },
  {
    title: 'Дата создания план-меню',
    colWidth: '10%',
    sort: 'created_at',
  },
  { title: 'Кол-во блюд', colWidth: '10%', sort: 'plan_menu_lines_count', align: 'right' },
]

export const PlanMenuPage = () => {
  const { t } = useTranslation()
  const [, setSearchParams] = useSearchParams()
  const navigate = useNavigate()

  const pageParams = useParamsControl<IPlanMenuRequest, keyof IPlanMenuRequest>(
    {
      withPagination: true,
      whiteList: [
        'search',
        'is_archived',
        'ordering',
        'plan_menu_type',
        'created_at_date_start',
        'created_at_date_end',
        'plan_menu_date_start_date',
        'plan_menu_date_end_date',
        'plan_menu_date_start_time',
        'plan_menu_date_end_time',
        'ids',
        'plan_menu_guest_type',
      ],
    },
  )

  const { data: planMenuList, isLoading: isLoadingGet } =
    useGetPlanMenuListQuery(
      {
        ...pageParams,
        ordering: pageParams?.ordering
          ? pageParams?.ordering
          : 'is_archived,-created_at',
      },
      { skip: !pageParams },
    )

  const clearFilterHandler = useCallback(
    () => setSearchParams('limit=40'),
    [setSearchParams],
  )
  const [chooseItems, setChooseItems] = useState<IChooseItems | null>(null)

  const [tableData, setTableData] = useState<IPlanMenuDto[]>([])

  const {
    deletePopup,
    closeDeletePopup,
    onDeletePopupHandler,
    onDeleteHandler,
  } = useDeletePlanMenu(chooseItems, setChooseItems)

  const [isFiltersOpen, setIsFiltersOpen] = useState(true)
  const { data: currentUser } = useGetCurrentUserQuery()

  const actions = useMemo(
    () => [
      {
        btnTitle: 'Добавить план-меню',
        btnIco: 'add',
        onClick: () => navigate(AllRoutes.addPlanMenu.path),
        color: AppBtnColor.whiteOutlineViolet,
        disabled:
          currentUser?.userRoles.restaurant.source.sourceType ===
          'EXT_INTEGRATION',
      },
      {
        btnTitle: 'Сбросить фильтр',
        onClick: clearFilterHandler,
        color: AppBtnColor.extraVioletViolet,
      },
    ],
    [clearFilterHandler, navigate],
  )

  const tableActions = [
    {
      title: 'Удалить',
      ico: 'basket',
      onClick: onDeletePopupHandler,
    },
  ]

  if (!planMenuList && isLoadingGet)
    return <AppLoader loaderType={LoaderType.main} />
  if (!planMenuList) return null

  return (
    <React.Fragment>
      {deletePopup && (
        <AppDeletePopup onClose={closeDeletePopup} onDelete={onDeleteHandler} />
      )}

      <div className={'width-for-page-wrapper plan-menu-page'}>
        <AppTitlePage
          title="План-меню"
          actions={actions}
          style={{ marginBottom: '20px' }}
          isFiltersOpen={isFiltersOpen}
          setIsFiltersOpen={setIsFiltersOpen}
        />
        {isFiltersOpen && (
          <div className="filter-block mt-20">
            <div className="col-3">
              <AppFilterMultiDropdownWithPagination<
                IPlanMenuTypeDto,
                keyof IPlanMenuTypeDto
              >
                searchParam={'plan_menu_type'}
                getterData={useGetPlanMenuTypeListQuery}
                propToShowInList={'name'}
                valuePropName={'id'}
                label={'Тип план-меню'}
                placeholder={'Выбрать'}
                fullWidth
              />
            </div>
            <div className="full-width-mob">
              <label>{t('Дата исполнения')}</label>
              <div className="input-row mt-10">
                <AppFilterDateInput searchParam={'plan_menu_date_start_date'} />
                <AppFilterDateInput searchParam={'plan_menu_date_end_date'} />
              </div>
            </div>
            <div className="full-width-mob">
              <label>{t('Время исполнения')}</label>
              <div className="input-row mt-10">
                <AppFilterTimeInput searchParam={'plan_menu_date_start_time'} />
                <AppFilterTimeInput searchParam={'plan_menu_date_end_time'} />
              </div>
            </div>
          </div>
        )}
        {isFiltersOpen && (
          <div className="filter-block">
            <div className="col-3">
              <AppFilterMultiDropdownWithPagination<
                IBaseSimpleList,
                keyof IBaseSimpleList
              >
                searchParam={'ids'}
                getterData={useGetPlanMenuSimpleListQuery}
                propToShowInList={'name'}
                valuePropName={'id'}
                label={'Название план-меню'}
                placeholder={'Выбрать'}
                fullWidth
              />
            </div>
            <div className="full-width-mob">
              <label>{t('Период создания')}</label>
              <div className="input-row mt-10">
                <AppFilterDateInput searchParam={'created_at_date_start'} />
                <AppFilterDateInput searchParam={'created_at_date_end'} />
              </div>
            </div>
            <AppFilterMultiDropdownWithPagination<
              IBaseSimpleList,
              keyof IBaseSimpleList
            >
              searchParam={'plan_menu_guest_type'}
              getterData={useGetGuestListTypeListQuery}
              propToShowInList={'name'}
              valuePropName={'id'}
              label={'Тип гостя'}
              placeholder={'Выбрать'}
            />
          </div>
        )}
        <AppTableMobile
          data={planMenuList}
          headerData={headerData}
          chooseMode
          chooseItems={chooseItems}
          setChooseItems={setChooseItems}
          chooseActions={tableActions}
          idName="plan-menu"
          setTableData={setTableData}
          additionalOrdering={'is_archived'}
          tableDataSelectors={[
            {
              renderItem: (item) => (
                <AppTableLink path={`${item.id}`} deleteItem={item.isArchived}>
                  {`${
                    item.dateStart
                      ? moment.parseZone(item.dateStart).format('DD.MM.YYYY')
                      : ''
                  } - ${
                    item.dateEnd
                      ? moment.parseZone(item.dateEnd).format('DD.MM.YYYY')
                      : ''
                  }`}
                </AppTableLink>
              ),
            },
            {
              renderItem: (item) => (
                <AppTableLink
                  path={`${item.id}`}
                  deleteItem={item.isArchived}
                >{`${
                  item.dateStart ? moment.parseZone(item.dateStart).format('HH:mm') : ''
                } - ${
                  item.dateEnd ? moment.parseZone(item.dateEnd).format('HH:mm') : ''
                }`}</AppTableLink>
              ),
            },
            {
              renderItem: (item) => (
                <AppTableLink path={`${item.id}`} deleteItem={item.isArchived}>
                  {item?.planMenuType?.name || ''}
                </AppTableLink>
              ),
            },
            {
              renderItem: (item) => (
                <AppTableLink path={`${item.id}`} deleteItem={item.isArchived}>
                  {item?.planMenuGuestType?.name || ''}
                </AppTableLink>
              ),
            },
            {
              renderItem: (item) => (
                <AppTableLink path={`${item.id}`} deleteItem={item.isArchived}>
                  {item.name}
                </AppTableLink>
              ),
            },
            {
              renderItem: (item) => (
                <AppTableLink
                  path={`${item.id}`}
                  deleteItem={item.isArchived}
                >{`${
                  item.createdAt
                    ? moment.parseZone(item.createdAt).format('DD.MM.YYYY')
                    : ''
                }`}</AppTableLink>
              ),
            },
            {
              renderItem: (item) => (
                <AppTableLink
                  path={`${item.id}`}
                  deleteItem={item.isArchived}
                  align={AppTableAlignType.right}
                >
                  <AppTableNumberBlock value={item.planMenuLinesCount} />
                </AppTableLink>
              ),
            },
          ]}
        />
      </div>
    </React.Fragment>
  )
}
